import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Badges";
import { inputCls } from "@/lib/records";

function Editor({ value, forms, onClose, onSaved }) {
  const [v, setV] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(value), [value]);
  if (!v) return null;
  const toggle = (id) => setV({ ...v, required_form_ids: v.required_form_ids.includes(id) ? v.required_form_ids.filter((x) => x !== id) : [...v.required_form_ids, id] });
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: v.name, required_form_ids: v.required_form_ids, active: v.active };
      v.id ? await api.put(`/admin/event-types/${v.id}`, body) : await api.post("/admin/event-types", body);
      toast.success("Event type saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="event-type-dialog">
        <DialogHeader><DialogTitle className="font-display">{v.id ? "Edit event type" : "New event type"}</DialogTitle></DialogHeader>
        <input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="e.g. Pet Registration" className={inputCls} data-testid="event-type-name-input" />
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">Required forms</p>
        <div className="space-y-2">{forms.map((f) => (
          <label key={f.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.required_form_ids.includes(f.id)} onChange={() => toggle(f.id)} data-testid={`event-type-form-${f.id}`} /> {f.title}</label>
        ))}</div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={v.active} onCheckedChange={(a) => setV({ ...v, active: a })} data-testid="event-type-active-switch" /> Active (available when logging)</label>
        <button onClick={save} disabled={busy || v.name.trim().length < 2} className="inline-flex items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="event-type-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
      </DialogContent>
    </Dialog>
  );
}

export default function EventTypes() {
  const [types, setTypes] = useState([]);
  const [forms, setForms] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/event-types").then((r) => setTypes(r.data)), []);
  useEffect(() => { load(); api.get("/forms").then((r) => setForms(r.data)); }, [load]);
  const title = Object.fromEntries(forms.map((f) => [f.id, f.title]));
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Admin" title="Required forms" description="Define resident events and the forms each one requires. Changes apply to records logged from now on."
        actions={<button onClick={() => setEdit({ name: "", required_form_ids: [], active: true })} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-4 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="event-type-add-button"><Plus className="h-4 w-4" /> Add event type</button>} />
      <div className="grid gap-4 md:grid-cols-2" data-testid="event-types-list">
        {types.map((t) => (
          <div key={t.id} className={`rounded-2xl border border-lasso-line bg-white p-5 ${t.active ? "" : "opacity-60"}`} data-testid={`event-type-${t.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-display text-lg font-bold">{t.name} {!t.active && <span className="ml-2 text-xs font-semibold text-zinc-500" data-testid={`event-type-inactive-${t.id}`}>Inactive</span>}</p>
              <button onClick={() => setEdit(t)} className="text-zinc-400 hover:text-lasso-ink" data-testid={`event-type-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">{t.required_form_ids.map((f) => <span key={f} className="rounded-full bg-lasso-tint px-2.5 py-1 text-xs font-medium">{title[f] || "Unavailable form"}</span>)}
              {!t.required_form_ids.length && <span className="text-xs text-zinc-400">No required forms</span>}</div>
          </div>
        ))}
      </div>
      <Editor value={edit} forms={forms} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
